/**
 * =============================================================================
 * ===== FILE: ui.js — ANTARMUKA (MENU / RACE HUD / HASIL) =====
 * =============================================================================
 * Membangun DOM, menangani tombol, daftar peserta, start race, podium.
 * Menghubungkan RaceEngine callbacks + audio.
 *
 * Layar:
 *   #menu-screen     — pengaturan peserta
 *   #race-screen     — canvas + HUD
 *   #results-screen  — juara 1–3 + ranking
 * =============================================================================
 */

/* GSAP dimuat dari vendor lokal (bukan node_modules) */
import gsap from 'gsap';
import { CONFIG, MODE_INFO, clamp, formatTime } from './config.js';
import { defaultName, randomName } from './names.js';
import { SKINS, randomSkinId, randomBodyColor, getSkin, colorCss } from './skins.js';
import { drawAntPreview } from './ant.js';
import { audio } from './audio.js';

/** Counter id unik untuk tiap peserta (naik terus selama sesi). */
let nextId = 1;

/**
 * createParticipant(index)
 * Buat objek peserta baru: id, nama default, skin acak, warna acak.
 */
export function createParticipant(index) {
  return {
    id: nextId++,
    name: defaultName(index),
    skinId: randomSkinId(),
    color: randomBodyColor(),
  };
}

/**
 * UIController — semua interaksi user & tampilan non-canvas.
 */
export class UIController {
  /**
   * constructor(root, engine)
   * root = #app, engine = RaceEngine
   */
  constructor(root, engine) {
    this.root = root;
    this.engine = engine;
    this.participants = [];
    this.mode = 'classic';
    this.theme = localStorage.getItem('ant-race-theme') || 'dark';
    document.documentElement.setAttribute('data-theme', this.theme);

    this.els = {};
    this._lastCd = '';
    this._toastTimer = 0;
    this._musicUnlocked = false;
    this._build();
    this._bind();
    this.resetParticipants(CONFIG.DEFAULT_ANTS);
    this.showScreen('menu');
  }

  /**
   * _build()
   * Inject seluruh HTML UI (menu, race, results) ke #app.
   */
  _build() {
    this.root.innerHTML = `
      <div class="ambient" aria-hidden="true"></div>
      <div id="boot"><div class="boot-inner">
        <div class="boot-ant">🐜</div>
        <h2>Ant Race</h2>
        <p style="color:var(--text-muted);font-weight:700;margin-top:4px">Menyiapkan arena...</p>
        <div class="boot-bar"><span></span></div>
      </div></div>

      <section id="menu-screen" class="screen active">
        <div class="menu-shell">
          <header class="brand">
            <div class="logo-wrap">
              <div class="logo-mark" aria-hidden="true">🐜</div>
              <div>
                <h1>Ant Race</h1>
                <p>Balapan semut RNG — siapa yang beruntung hari ini?</p>
              </div>
            </div>
            <div class="top-actions">
              <button type="button" class="icon-btn" id="btn-theme" title="Dark/Light">🌙</button>
              <button type="button" class="icon-btn" id="btn-sound" title="Suara">🔊</button>
            </div>
          </header>

          <div class="panel">
            <div class="panel-header">
              <h2>Peserta Balapan</h2>
              <div class="controls-row">
                <div class="chip">
                  <span>Jumlah</span>
                  <div class="stepper">
                    <button type="button" id="btn-dec" aria-label="Kurangi">−</button>
                    <span id="count-label">8</span>
                    <button type="button" id="btn-inc" aria-label="Tambah">+</button>
                  </div>
                </div>
                <select class="mode-select" id="mode-select" aria-label="Mode balapan">
                  ${Object.entries(MODE_INFO)
                    .map(([k, v]) => `<option value="${k}">${v.label}</option>`)
                    .join('')}
                </select>
              </div>
            </div>
            <div class="ant-list-wrap">
              <div class="ant-grid" id="ant-grid"></div>
            </div>
          </div>

          <footer class="footer-bar">
            <div class="footer-left">
              <button type="button" class="btn btn-ghost" id="btn-rand-names">🎲 Nama</button>
              <button type="button" class="btn btn-ghost" id="btn-rand-skins">🎭 Skin</button>
              <button type="button" class="btn btn-ghost" id="btn-rand-colors">🎨 Warna</button>
              <button type="button" class="btn btn-secondary" id="btn-shuffle">🔀 Shuffle</button>
              <button type="button" class="btn btn-danger" id="btn-reset">↺ Reset</button>
            </div>
            <div class="footer-right">
              <p class="mode-hint" id="mode-hint"></p>
              <button type="button" class="btn btn-primary btn-lg" id="btn-start">🏁 Start Race</button>
            </div>
          </footer>
        </div>
      </section>

      <section id="race-screen" class="screen">
        <canvas id="game-canvas"></canvas>
        <div class="hud">
          <div class="hud-top">
            <div class="hud-card">
              <div class="hud-title" id="hud-mode">Classic Race</div>
              <div class="hud-sub" id="hud-meta">0:00 · 8 ants</div>
            </div>
            <div class="hud-card leaderboard-mini" id="hud-lb"></div>
          </div>
          <div class="hud-bottom">
            <div class="hud-card progress-wrap">
              <div class="hud-sub" style="margin-bottom:6px">Progress pemimpin</div>
              <div class="progress-bar"><div class="progress-fill" id="hud-progress"></div></div>
            </div>
            <div class="hud-actions">
              <button type="button" class="btn btn-ghost" id="btn-skip">Lewati ⏭</button>
              <button type="button" class="btn btn-danger" id="btn-quit">Keluar</button>
            </div>
          </div>
        </div>
        <div class="countdown-overlay" id="countdown-overlay">
          <div class="countdown-text" id="countdown-text">3</div>
        </div>
        <div class="round-banner" id="round-banner"></div>
      </section>

      <section id="results-screen" class="screen">
        <div class="results-card">
          <div class="results-header">
            <div class="trophy">🏆</div>
            <h2>Hasil Balapan</h2>
            <p id="results-sub">Classic Race</p>
          </div>
          <div class="podium" id="podium"></div>

          <div class="full-rankings">
            <table class="rank-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Nama</th>
                  <th>Skin</th>
                  <th id="th-metric">Waktu</th>
                </tr>
              </thead>
              <tbody id="rank-body"></tbody>
            </table>
          </div>
          <div class="results-actions">
            <button type="button" class="btn btn-primary btn-lg" id="btn-rematch">🔁 Rematch</button>
            <button type="button" class="btn btn-secondary btn-lg" id="btn-to-menu">🏠 Menu</button>
          </div>
        </div>
      </section>

      <canvas id="fx-canvas"></canvas>
      <div class="toast" id="toast"></div>
    `;

    const q = (sel) => this.root.querySelector(sel);
    this.els = {
      boot: q('#boot'),
      menu: q('#menu-screen'),
      race: q('#race-screen'),
      results: q('#results-screen'),
      grid: q('#ant-grid'),
      count: q('#count-label'),
      mode: q('#mode-select'),
      hint: q('#mode-hint'),
      canvas: q('#game-canvas'),
      hudMode: q('#hud-mode'),
      hudMeta: q('#hud-meta'),
      hudLb: q('#hud-lb'),
      hudProgress: q('#hud-progress'),
      cdOverlay: q('#countdown-overlay'),
      cdText: q('#countdown-text'),
      banner: q('#round-banner'),
      podium: q('#podium'),
      rankBody: q('#rank-body'),
      resultsSub: q('#results-sub'),
      thMetric: q('#th-metric'),
      toast: q('#toast'),
      fx: q('#fx-canvas'),
      themeBtn: q('#btn-theme'),
      soundBtn: q('#btn-sound'),
    };

    this.engine.attachCanvas(this.els.canvas);
  }

  /**
   * _bind()
   * Pasang semua event listener tombol + callback engine + unlock audio.
   */
  _bind() {
    const e = this.els;

    this.root.querySelector('#btn-inc').onclick = () => {
      this._unlockMusic();
      audio.click();
      this.setCount(this.participants.length + 1);
    };
    this.root.querySelector('#btn-dec').onclick = () => {
      this._unlockMusic();
      audio.click();
      this.setCount(this.participants.length - 1);
    };
    this.root.querySelector('#btn-rand-names').onclick = () => {
      this._unlockMusic();
      audio.click();
      const used = new Set();
      this.participants.forEach((p) => {
        p.name = randomName(used);
      });
      this.renderList();
      this.toast('Nama diacak!');
    };
    this.root.querySelector('#btn-rand-skins').onclick = () => {
      audio.click();
      this.participants.forEach((p) => {
        p.skinId = randomSkinId();
      });
      this.renderList();
      this.toast('Skin diacak!');
    };
    this.root.querySelector('#btn-rand-colors').onclick = () => {
      audio.click();
      this.participants.forEach((p) => {
        p.color = randomBodyColor();
      });
      this.renderList();
      this.toast('Warna diacak!');
    };
    this.root.querySelector('#btn-shuffle').onclick = () => {
      audio.click();
      for (let i = this.participants.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [this.participants[i], this.participants[j]] = [
          this.participants[j],
          this.participants[i],
        ];
      }
      this.renderList();
      this.toast('Urutan di-shuffle!');
    };
    this.root.querySelector('#btn-reset').onclick = () => {
      audio.click();
      this.resetParticipants(CONFIG.DEFAULT_ANTS);
      this.mode = 'classic';
      e.mode.value = 'classic';
      this._updateHint();
      this.toast('Direset ke default');
    };
    this.root.querySelector('#btn-start').onclick = () => this.startRace();
    this.root.querySelector('#btn-quit').onclick = () => this.backToMenu();
    this.root.querySelector('#btn-skip').onclick = () => this.skipToFinish();
    this.root.querySelector('#btn-rematch').onclick = () => this.startRace();
    this.root.querySelector('#btn-to-menu').onclick = () => this.backToMenu();

    e.mode.onchange = () => {
      this.mode = e.mode.value;
      this._updateHint();
      audio.click();
    };

    e.themeBtn.onclick = () => {
      this.theme = this.theme === 'dark' ? 'light' : 'dark';
      document.documentElement.setAttribute('data-theme', this.theme);
      localStorage.setItem('ant-race-theme', this.theme);
      e.themeBtn.textContent = this.theme === 'dark' ? '🌙' : '☀️';
      audio.click();
    };
    e.themeBtn.textContent = this.theme === 'dark' ? '🌙' : '☀️';

    e.soundBtn.onclick = () => {
      this._unlockMusic();
      const on = audio.toggle();
      e.soundBtn.textContent = on ? '🔊' : '🔇';
      if (on) {
        audio.click();
        // Resume appropriate track for current screen
        if (this.els.menu.classList.contains('active')) audio.playMusic('lobby');
        else if (this.els.results.classList.contains('active')) audio.playMusic('victory');
        else if (this.els.race.classList.contains('active')) audio.playMusic('race');
      }
    };

    this.engine.setCallbacks({
      onState: (state) => this._onRaceState(state),
      onHud: (hud) => this._onHud(hud),
      onFinish: (results, meta) => this.showResults(results, meta),
      onBanner: (text) => this.showBanner(text),
    });

    window.addEventListener('resize', () => {
      if (this.engine) this.engine.resize();
    });

    // Unlock audio + lobby music on first user gesture (browser autoplay policy)
    const unlock = () => {
      this._unlockMusic();
    };
    ['pointerdown', 'touchstart', 'keydown', 'click'].forEach((ev) => {
      window.addEventListener(ev, unlock, { once: true, capture: true });
    });

    // Also try when menu buttons are used (capture already on window)
    this.root.addEventListener(
      'pointerdown',
      () => {
        this._unlockMusic();
      },
      { capture: true }
    );
  }

  /**
   * _unlockMusic()
   * Browser butuh gesture user sebelum AudioContext boleh bunyi.
   * Setelah unlock, putar musik sesuai layar aktif.
   */
  async _unlockMusic() {
    if (this._musicUnlocked) {
      await audio.ensure();
      return;
    }
    this._musicUnlocked = true;
    await audio.ensure();

    // Always start the right track after unlock
    if (this.els.results?.classList.contains('active')) {
      await audio.playMusic('victory', true);
    } else if (this.els.race?.classList.contains('active')) {
      await audio.playMusic('race', true);
    } else {
      await audio.playMusic('lobby', true);
      this.toast('🎵 Musik lobby aktif');
    }
  }

  bootDone() {
    const boot = this.els.boot;
    if (!boot) return;
    boot.classList.add('hide');
    setTimeout(() => boot.remove(), 500);
    // Hint: browsers block audio until tap
    setTimeout(() => {
      if (!this._musicUnlocked && this.els.menu?.classList.contains('active')) {
        this.toast('Ketuk layar untuk musik 🎵');
      }
    }, 900);
  }

  showScreen(name) {
    this.els.menu.classList.toggle('active', name === 'menu');
    this.els.race.classList.toggle('active', name === 'race');
    this.els.results.classList.toggle('active', name === 'results');

    /*
     * Scroll halaman:
     * - menu / results → boleh scroll (konten panjang tidak terpotong)
     * - race → page-lock (canvas fullscreen, tidak ikut scroll)
     */
    /* page-lock: fullscreen race; seo-play-mode: sembunyikan landing SEO */
    document.body.classList.toggle('page-lock', name === 'race');
    if (name === 'race' || name === 'results') {
      document.documentElement.classList.add('seo-play-mode');
    }
    if (name !== 'race') {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }

    if (this._musicUnlocked && audio.enabled) {
      if (name === 'menu') audio.playMusic('lobby');
      else if (name === 'race') audio.playMusic('race');
      else if (name === 'results') audio.playMusic('victory');
    }
  }

  resetParticipants(n) {
    nextId = 1;
    this.participants = [];
    for (let i = 0; i < n; i++) this.participants.push(createParticipant(i));
    this.renderList();
  }

  setCount(n) {
    n = clamp(n, CONFIG.MIN_ANTS, CONFIG.MAX_ANTS);
    const cur = this.participants.length;
    if (n > cur) {
      for (let i = cur; i < n; i++) this.participants.push(createParticipant(i));
    } else if (n < cur) {
      this.participants.length = n;
    }
    this.renderList();
  }

  removeParticipant(id) {
    if (this.participants.length <= CONFIG.MIN_ANTS) {
      this.toast(`Minimal ${CONFIG.MIN_ANTS} semut`);
      return false;
    }
    const idx = this.participants.findIndex((p) => p.id === id);
    if (idx < 0) return false;
    const removed = this.participants[idx];
    this.participants.splice(idx, 1);
    this.renderList();
    this.toast(`${removed.name} dihapus`);
    return true;
  }

  /**
   * renderList()
   * Render ulang grid kartu peserta (preview canvas, nama, aksi, hapus).
   */
  renderList() {
    this.els.count.textContent = String(this.participants.length);
    const grid = this.els.grid;
    grid.innerHTML = '';

    const canDelete = this.participants.length > CONFIG.MIN_ANTS;
    const frag = document.createDocumentFragment();
    this.participants.forEach((p, idx) => {
      const skin = getSkin(p.skinId);
      const card = document.createElement('div');
      card.className = 'ant-card';
      card.innerHTML = `
        <div class="ant-preview"><canvas width="52" height="52"></canvas></div>
        <div class="ant-meta">
          <input type="text" maxlength="24" value="${escapeAttr(p.name)}" data-id="${p.id}" aria-label="Nama semut ${idx + 1}" />
          <div class="ant-skin-label">${skin.emoji === 'hoodie' ? '👕' : skin.emoji} ${skin.label}</div>
        </div>
        <div class="ant-card-actions">
          <button type="button" class="mini-btn" data-act="skin" data-id="${p.id}" title="Ganti skin">🎭</button>
          <button type="button" class="mini-btn" data-act="color" data-id="${p.id}" title="Ganti warna">🎨</button>
          <button type="button" class="mini-btn" data-act="name" data-id="${p.id}" title="Nama acak">🎲</button>
          <button type="button" class="mini-btn mini-btn-danger" data-act="delete" data-id="${p.id}" title="Hapus semut" aria-label="Hapus ${escapeAttr(p.name)}" ${canDelete ? '' : 'disabled'}>🗑️</button>
        </div>
      `;
      const canvas = card.querySelector('canvas');
      drawAntPreview(canvas, p);

      const input = card.querySelector('input');
      input.addEventListener('change', () => {
        const v = input.value.trim() || defaultName(idx);
        p.name = v.slice(0, 24);
        input.value = p.name;
      });
      input.addEventListener('keydown', (ev) => {
        if (ev.key === 'Enter') input.blur();
      });

      card.querySelectorAll('.mini-btn').forEach((btn) => {
        btn.onclick = () => {
          this._unlockMusic();
          audio.click();
          const act = btn.getAttribute('data-act');
          if (act === 'skin') {
            const i = SKINS.findIndex((s) => s.id === p.skinId);
            p.skinId = SKINS[(i + 1) % SKINS.length].id;
            this.renderList();
          } else if (act === 'color') {
            p.color = randomBodyColor();
            this.renderList();
          } else if (act === 'name') {
            p.name = randomName();
            this.renderList();
          } else if (act === 'delete') {
            this.removeParticipant(p.id);
          }
        };
      });

      frag.appendChild(card);
    });
    grid.appendChild(frag);
    this._updateHint();
  }

  _updateHint() {
    this.els.hint.textContent = MODE_INFO[this.mode]?.hint || '';
  }

  /**
   * startRace()
   * Validasi jumlah → pindah ke race screen → setup engine → countdown.
   */
  async startRace() {
    if (this.participants.length < CONFIG.MIN_ANTS) {
      this.toast('Minimal 2 semut');
      return;
    }
    await this._unlockMusic();
    audio.click();
    this._lastCd = '';
    this.showScreen('race');
    // Force energetic race BGM (different from lobby)
    await audio.playMusic('race', true);

    requestAnimationFrame(() => {
      this.engine.resize();
      const list = this.participants.map((p) => ({
        id: p.id,
        name: p.name,
        skinId: p.skinId,
        color: { ...p.color },
      }));

      if (this.mode === 'relay') {
        this.engine.startRelayTournament(list);
      } else {
        this.engine.setup(list, this.mode);
        this.engine.start();
      }
    });
  }

  async backToMenu() {
    await this._unlockMusic();
    audio.click();
    this.engine.stop();
    this._lastCd = '';
    this.els.cdOverlay.classList.remove('show');
    this.showScreen('menu');
    this.renderList();
    await audio.playMusic('lobby', true);
  }

  skipToFinish() {
    if (this.engine.state !== 'racing' && this.engine.state !== 'countdown') return;
    audio.click();
    this.engine.skipToEnd();
  }

  _onRaceState(state) {
    const ov = this.els.cdOverlay;
    if (state === 'countdown') {
      ov.classList.add('show');
    } else {
      ov.classList.remove('show');
      gsap.killTweensOf(this.els.cdText);
      this.els.cdText.style.opacity = '0';
      this._lastCd = '';
    }
  }

  _onHud(hud) {
    this.els.hudMode.textContent =
      hud.mode === 'relay'
        ? `${hud.modeLabel} · R${hud.relayRound}/${CONFIG.RELAY_ROUNDS}`
        : hud.modeLabel;
    this.els.hudMeta.textContent =
      hud.mode === 'survival'
        ? `${hud.timeText} · ${hud.alive}/${hud.total} hidup`
        : `${hud.timeText} · ${hud.total} semut`;

    this.els.hudProgress.style.width = `${Math.round(hud.leaderProgress * 100)}%`;

    this.els.hudLb.innerHTML = hud.board
      .map(
        (r) => `
        <div class="lb-row${r.finished ? ' lb-finished' : ''}">
          <span class="lb-pos">${r.pos}</span>
          <span class="lb-dot" style="background:${colorCss(r.color)}"></span>
          <span class="lb-name">${escapeHtml(r.name)}${r.finished ? ' ✓' : ''}</span>
        </div>`
      )
      .join('');

    if (hud.countdown) {
      const t = this.els.cdText;
      if (this._lastCd !== hud.countdown) {
        this._lastCd = hud.countdown;
        t.textContent = hud.countdown;
        this.els.cdOverlay.classList.add('show');
        gsap.fromTo(
          t,
          { scale: 0.4, opacity: 0 },
          { scale: 1.1, opacity: 1, duration: 0.35, ease: 'back.out(2)' }
        );
        gsap.to(t, { scale: 1, duration: 0.2, delay: 0.35 });
      }
    }
  }

  showBanner(text) {
    const b = this.els.banner;
    b.textContent = text;
    gsap.killTweensOf(b);
    gsap.fromTo(
      b,
      { opacity: 0, y: -16 },
      { opacity: 1, y: 0, duration: 0.35, ease: 'power2.out' }
    );
    gsap.to(b, { opacity: 0, y: -10, delay: 1.8, duration: 0.4 });
  }

  /**
   * showResults(results, meta)
   * Tampilkan podium 1–3 + tabel ranking lengkap.
   */
  showResults(results, meta) {
    this.showScreen('results');
    // Victory bed under applause (finish SFX already fired from engine)
    audio.playMusic('victory', true);
    const isRelay = meta.mode === 'relay';
    this.els.resultsSub.textContent = isRelay
      ? `${MODE_INFO.relay.label} · ${CONFIG.RELAY_ROUNDS} ronde`
      : `${MODE_INFO[meta.mode]?.label || ''} · ${formatTime(meta.elapsed)}`;
    this.els.thMetric.textContent = isRelay ? 'Poin' : 'Waktu';

    const slots = [
      { r: results[1], cls: 'second', place: '🥈 2' },
      { r: results[0], cls: 'first', place: '🥇 1' },
      { r: results[2], cls: 'third', place: '🥉 3' },
    ];

    this.els.podium.innerHTML = slots
      .map(({ r, cls, place }) => {
        if (!r) {
          return `<div class="podium-slot ${cls}"><div class="podium-place">—</div></div>`;
        }
        const metric = isRelay ? `${r.points} pts` : formatTime(r.time);
        return `
          <div class="podium-slot ${cls}">
            <div class="podium-swatch" style="background:${colorCss(r.color)}"></div>
            <div class="podium-place">${place}</div>
            <div class="podium-name">${escapeHtml(r.name)}</div>
            <div class="podium-time">${metric}</div>
          </div>`;
      })
      .join('');

    this.els.rankBody.innerHTML = results
      .map((r) => {
        const skin = getSkin(r.skinId);
        const metric = r.eliminated
          ? 'OUT'
          : isRelay
            ? `${r.points} pts`
            : formatTime(r.time);
        return `<tr>
          <td>${r.rank}</td>
          <td>${escapeHtml(r.name)}</td>
          <td>${skin.emoji === 'hoodie' ? '👕' : skin.emoji} ${skin.label}</td>
          <td>${metric}</td>
        </tr>`;
      })
      .join('');

    gsap.from('.results-card', { y: 40, opacity: 0, duration: 0.45, ease: 'power3.out' });
    gsap.from('.podium-slot', {
      y: 24,
      opacity: 0,
      duration: 0.4,
      stagger: 0.08,
      delay: 0.15,
    });
  }

  toast(msg) {
    const t = this.els.toast;
    t.textContent = msg;
    t.classList.add('show');
    clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => t.classList.remove('show'), 1800);
  }
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function escapeAttr(s) {
  return escapeHtml(s).replace(/'/g, '&#39;');
}
